import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import SEOHelmet from '../../components/SEOHelmet';
import { motion, AnimatePresence } from 'framer-motion';
import PublicNavigation from '../../components/ui/PublicNavigation';
import UnifiedFooter from '../../components/ui/UnifiedFooter';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { catalogService } from '../../services/catalogService';
import { supabase } from '../../lib/supabase';

export default function PublicMaritimeOrderPage() {
  // Tab state
  const [activeTab, setActiveTab] = useState('order'); // 'order' or 'pickup'

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);

  // Order form data (for modal)
  const [orderFormData, setOrderFormData] = useState({
    quantity: 1,
    customerName: '',
    customerEmail: '',
    customerPhone: '',
    deliveryAddress: '',
    postalCode: '',
    city: '',
    floor: '',
    deliveryDate: '',
    deliveryTimeSlot: 'Matin 8h-12h',
    paymentMethod: 'Virement bancaire',
    acceptTerms: false
  });

  // Pickup form data (for pickup reservation)
  const [pickupFormData, setPickupFormData] = useState({
    customerName: '',
    customerEmail: '',
    customerPhone: '',
    customerAddress: '',
    postalCode: '',
    customerCity: '',
    floor: '',
    deliveryDate: '',
    deliveryTimeSlot: 'Matin 8h-12h',
    paymentMethod: 'Virement bancaire',
    acceptTerms: false
  });

  const [selectedArticles, setSelectedArticles] = useState([]);
  const [catalogItems, setCatalogItems] = useState([]);
  const [selectedCatalogItemId, setSelectedCatalogItemId] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingCatalog, setLoadingCatalog] = useState(true);
  const [errors, setErrors] = useState({});
  const [openFaq, setOpenFaq] = useState(null);
  const [showPromoBanner, setShowPromoBanner] = useState(true);
  const [isPromoOrder, setIsPromoOrder] = useState(false);

  const generateReference = (prefix) => {
    const timestamp = Date.now();
    const random = Math.floor(Math.random() * 1000);
    return `${prefix}-${timestamp}-${random}`;
  };

  const handleAddArticle = () => {
    if (!selectedCatalogItemId) return;
    
    const catalogItem = catalogItems?.find(item => item?.id === selectedCatalogItemId);
    if (!catalogItem) return;

    const existingArticle = selectedArticles?.find(a => a?.catalogItemId === selectedCatalogItemId);
    
    if (existingArticle) {
      setSelectedArticles(prev => 
        prev?.map(a => 
          a?.catalogItemId === selectedCatalogItemId 
            ? { ...a, quantity: a?.quantity + 1 }
            : a
        )
      );
    } else {
      setSelectedArticles(prev => [
        ...prev,
        {
          catalogItemId: catalogItem?.id,
          name: catalogItem?.name,
          price: catalogItem?.price,
          quantity: 1
        }
      ]);
    }
    
    setSelectedCatalogItemId('');
  };

  const handleQuantityChange = (catalogItemId, delta) => {
    setSelectedArticles(prev => 
      prev?.map(article => 
        article?.catalogItemId === catalogItemId
          ? { ...article, quantity: Math.max(1, article?.quantity + delta) }
          : article
      )
    );
  };

  const handleRemoveArticle = (catalogItemId) => {
    setSelectedArticles(prev => prev?.filter(a => a?.catalogItemId !== catalogItemId));
  };

  const calculatePickupTotal = () => {
    return selectedArticles?.reduce((total, article) => {
      return total + (article?.price * article?.quantity);
    }, 0) || 0;
  };

  const validatePickupForm = () => {
    const newErrors = {};

    if (!pickupFormData?.customerName?.trim()) newErrors.customerName = 'Nom complet requis';
    if (!pickupFormData?.customerEmail?.trim()) newErrors.customerEmail = 'Email requis';
    if (!pickupFormData?.customerPhone?.trim()) newErrors.customerPhone = 'Téléphone requis';
    if (!pickupFormData?.customerAddress?.trim()) newErrors.customerAddress = 'Adresse requise';
    if (!pickupFormData?.postalCode?.trim()) newErrors.postalCode = 'Code postal requis';
    if (!pickupFormData?.customerCity?.trim()) newErrors.customerCity = 'Ville requise';
    if (!pickupFormData?.deliveryDate) newErrors.deliveryDate = 'Date d\'enlèvement requise';
    if (selectedArticles?.length === 0) newErrors.articles = 'Veuillez ajouter au moins un article';
    if (!pickupFormData?.acceptTerms) newErrors.acceptTerms = 'Vous devez accepter les CGV';

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handlePickupSubmit = async (e) => {
    e?.preventDefault();

    if (!validatePickupForm()) {
      alert('Veuillez remplir tous les champs obligatoires');
      return;
    }

    setLoading(true);

    try {
      const reference = generateReference('ENLEV');
      const totalAmount = calculatePickupTotal();

      const articlesDescription = selectedArticles?.map(a => `${a?.name} (x${a?.quantity})`)?.join(', ');

      // Insert into barrel_sales table
      const { data, error } = await supabase?.from('barrel_sales')?.insert({
        customer_name: pickupFormData?.customerName,
        customer_phone: pickupFormData?.customerPhone,
        customer_email: pickupFormData?.customerEmail,
        customer_address: pickupFormData?.customerAddress,
        customer_city: pickupFormData?.customerCity,
        postal_code: pickupFormData?.postalCode,
        barrel_type: 'noir_270l',
        quantity: selectedArticles?.length,
        unit_price: totalAmount / (selectedArticles?.length || 1),
        total_price: totalAmount,
        delivery_date: pickupFormData?.deliveryDate || null,
        delivery_time_slot: pickupFormData?.deliveryTimeSlot,
        payment_status: 'pending',
        order_status: 'pending',
        notes: `Réservation enlèvement - Étage/Digicode: ${pickupFormData?.floor || 'N/A'} - Articles: ${articlesDescription} - Paiement: ${pickupFormData?.paymentMethod} - Référence: ${reference}`
      })?.select()?.single();

      if (error) throw error;

      alert(`✅ Réservation d'enlèvement confirmée !

Votre numéro de référence : ${reference}

Vous recevrez un email de confirmation avec tous les détails.`);

      // Reset form
      setPickupFormData({
        customerName: '',
        customerEmail: '',
        customerPhone: '',
        customerAddress: '',
        postalCode: '',
        customerCity: '',
        floor: '',
        deliveryDate: '',
        deliveryTimeSlot: 'Matin 8h-12h',
        paymentMethod: 'Virement bancaire',
        acceptTerms: false
      });
      setSelectedArticles([]);
      setSelectedCatalogItemId('');
      setErrors({});

    } catch (error) {
      console.error('Error submitting pickup reservation:', error);
      alert('Une erreur est survenue lors de l\'enregistrement de votre réservation. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  const handlePickupInputChange = (field) => (e) => {
    const value = field === 'acceptTerms' ? e?.target?.checked : e?.target?.value;
    setPickupFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const containerCards = [
    {
      id: 'barrique-noire',
      badge: 'La plus populaire',
      badgeColor: 'bg-green-600',
      title: 'Barrique Noire',
      capacity: '270 litres',
      basePrice: 30,
      banlieuePrice: 35,
      prices: [
        { label: 'Barrique vide Paris intra-muros', value: 30 },
        { label: 'Barrique vide Banlieue', value: 35 },
        { label: 'Transport vers Abidjan (hors promo)', value: 150 }
      ],
      totalFrom: 180,
      features: [
        'Plus grande capacité (270L)',
        'Idéale pour les gros volumes',
        'Offre Été 2026 : 160€ TTC tout compris avec barrique offerte'
      ],
      icon: 'Package'
    },
    {
      id: 'barrique-bleue',
      badge: 'La plus résistante',
      badgeColor: 'bg-blue-600',
      title: 'Barrique Bleue',
      capacity: '220 litres',
      basePrice: 40,
      banlieuePrice: 45,
      prices: [
        { label: 'Barrique vide Paris intra-muros', value: 40 },
        { label: 'Barrique vide Banlieue', value: 45 },
        { label: 'Transport vers Abidjan (remplie)', value: 140 }
      ],
      totalFrom: 180,
      features: [
        'Plus résistante et sécurisée avec cerclage en fer',
        'Transport hors promo : 140€',
        'Idéale pour objets fragiles ou volumineux'
      ],
      icon: 'Package'
    },
    {
      id: 'carton-petit',
      badge: 'Idéal premiers envois',
      badgeColor: 'bg-orange-600',
      title: 'Carton Petit',
      capacity: '80 x 60 x 60 cm',
      basePrice: 90,
      prices: [
        { label: 'Prix tout compris (carton + transport)', value: 90 }
      ],
      totalFrom: 90,
      features: [
        'Triple cannelure ultra-résistante',
        'Parfait pour petits appareils neufs et objets contenant du liquide',
        'Remplissage facile grâce à ouverture large',
        'Transport vers Abidjan inclus'
      ],
      icon: 'Package'
    },
    {
      id: 'carton-moyen',
      badge: 'Le plus commandé',
      badgeColor: 'bg-green-600',
      title: 'Carton Moyen',
      capacity: '100 x 55 x 55 cm',
      basePrice: 120,
      prices: [
        { label: 'Prix tout compris (carton + transport)', value: 120 }
      ],
      totalFrom: 120,
      features: [
        'Triple cannelure ultra-résistante',
        'Taille idéale pour objets fragiles et électroménager petit format',
        'Protège vos appareils neufs grâce à épaisseur renforcée',
        'Transport vers Abidjan inclus'
      ],
      icon: 'Package'
    },
    {
      id: 'carton-grand',
      badge: 'Maximum de protection',
      badgeColor: 'bg-blue-600',
      title: 'Carton Grand',
      capacity: '120 x 60 x 60 cm',
      basePrice: 150,
      prices: [
        { label: 'Prix tout compris (carton + transport)', value: 150 }
      ],
      totalFrom: 150,
      features: [
        'Triple cannelure ultra-résistante',
        'Le plus grand format pour objets fragiles et volumineux',
        'Idéal pour écrans, TV, et appareils électroménagers neufs',
        'Transport vers Abidjan inclus'
      ],
      icon: 'Package'
    }
  ];

  useEffect(() => {
    loadCatalogItems();
  }, []);

  const loadCatalogItems = async () => {
    try {
      const items = await catalogService?.getAll();
      setCatalogItems(items || []);
    } catch (err) {
      console.error('Error loading catalog:', err);
    } finally {
      setLoadingCatalog(false);
    }
  };

  const handlePromoSelection = () => {
    setSelectedProduct(containerCards?.[0]); // Barrique noire is first
    setOrderFormData(prev => ({
      ...prev,
      quantity: 1
    }));
    setIsModalOpen(true);
    setShowPromoBanner(false);
    setIsPromoOrder(true);
  };

  const handleOpenOrderModal = (card) => {
    setSelectedProduct(card);
    setOrderFormData(prev => ({
      ...prev,
      quantity: 1
    }));
    setIsModalOpen(true);
    setIsPromoOrder(false);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedProduct(null);
    setIsPromoOrder(false);
    setErrors({});
  };

  const handleOrderInputChange = (field) => (e) => {
    const value = field === 'acceptTerms' ? e?.target?.checked : e?.target?.value;
    setOrderFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const calculateOrderTotal = () => {
    if (isPromoOrder && selectedProduct?.id === 'barrique-noire') {
      return 160 * orderFormData?.quantity;
    }
    
    if (selectedProduct?.id === 'barrique-noire' && orderFormData?.postalCode) {
      const unitPrice = orderFormData?.postalCode?.startsWith('75') && orderFormData?.postalCode?.length === 5 ? 30 : 35;
      return unitPrice * orderFormData?.quantity;
    }
    
    return (selectedProduct?.basePrice || 0) * orderFormData?.quantity;
  };

  const validateOrderForm = () => {
    const newErrors = {};

    if (!orderFormData?.customerName?.trim()) newErrors.customerName = 'Nom complet requis';
    if (!orderFormData?.customerEmail?.trim()) newErrors.customerEmail = 'Email requis';
    if (!orderFormData?.customerPhone?.trim()) newErrors.customerPhone = 'Téléphone requis';
    if (!orderFormData?.deliveryDate) newErrors.deliveryDate = 'Date de livraison requise';
    if (!orderFormData?.acceptTerms) newErrors.acceptTerms = 'Vous devez accepter les CGV';

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleOrderSubmit = async (e) => {
    e?.preventDefault();

    if (!validateOrderForm()) {
      alert('Veuillez remplir tous les champs obligatoires');
      return;
    }

    setLoading(true);

    try {
      const reference = generateReference('CMD');
      const totalAmount = calculateOrderTotal();

      // Map product id to valid barrel_type enum values
      const barrelTypeMap = {
        'barrique-noire': 'noir_270l',
        'barrique-bleue': 'bleu_220l'
      };
      const isBarrel = selectedProduct?.id === 'barrique-noire' || selectedProduct?.id === 'barrique-bleue';
      const barrelType = barrelTypeMap?.[selectedProduct?.id] || 'noir_270l';

      let insertData;
      if (isBarrel) {
        // Insert into barrel_sales table for barrel orders
        insertData = await supabase?.from('barrel_sales')?.insert({
          customer_name: orderFormData?.customerName,
          customer_phone: orderFormData?.customerPhone,
          customer_email: orderFormData?.customerEmail,
          customer_address: orderFormData?.deliveryAddress,
          customer_city: orderFormData?.city,
          postal_code: orderFormData?.postalCode,
          barrel_type: barrelType,
          quantity: orderFormData?.quantity,
          unit_price: totalAmount / orderFormData?.quantity,
          total_price: totalAmount,
          delivery_date: orderFormData?.deliveryDate || null,
          delivery_time_slot: orderFormData?.deliveryTimeSlot,
          payment_status: 'pending',
          order_status: 'pending',
          notes: `Commande ${selectedProduct?.title} - Étage/Digicode: ${orderFormData?.floor || 'N/A'} - Paiement: ${orderFormData?.paymentMethod} - Référence: ${reference}${isPromoOrder ? ' - OFFRE ÉTÉ 2026' : ''}`
        })?.select()?.single();
      } else {
        // For cartons and other products, insert into barrel_sales with noir_270l as default type
        // and use notes to record the actual product
        insertData = await supabase?.from('barrel_sales')?.insert({
          customer_name: orderFormData?.customerName,
          customer_phone: orderFormData?.customerPhone,
          customer_email: orderFormData?.customerEmail,
          customer_address: orderFormData?.deliveryAddress,
          customer_city: orderFormData?.city,
          postal_code: orderFormData?.postalCode,
          barrel_type: 'noir_270l',
          quantity: orderFormData?.quantity,
          unit_price: totalAmount / orderFormData?.quantity,
          total_price: totalAmount,
          delivery_date: orderFormData?.deliveryDate || null,
          delivery_time_slot: orderFormData?.deliveryTimeSlot,
          payment_status: 'pending',
          order_status: 'pending',
          notes: `Commande ${selectedProduct?.title} - Étage/Digicode: ${orderFormData?.floor || 'N/A'} - Paiement: ${orderFormData?.paymentMethod} - Référence: ${reference}${isPromoOrder ? ' - OFFRE ÉTÉ 2026' : ''}`
        })?.select()?.single();
      }

      const { data, error } = insertData;
      if (error) throw error;

      // Send confirmation email
      try {
        const emailResponse = await fetch(
          `${import.meta.env?.VITE_SUPABASE_URL}/functions/v1/send-barrel-order-confirmation`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${import.meta.env?.VITE_SUPABASE_ANON_KEY}`
            },
            body: JSON.stringify({
              name: orderFormData?.customerName,
              email: orderFormData?.customerEmail,
              reference: reference,
              barrelType: selectedProduct?.title,
              quantity: orderFormData?.quantity,
              totalAmount: totalAmount,
              articles: [
                {
                  name: selectedProduct?.title,
                  quantity: orderFormData?.quantity,
                  price: totalAmount / (orderFormData?.quantity || 1)
                }
              ],
              deliveryDate: orderFormData?.deliveryDate,
              deliveryTimeSlot: orderFormData?.deliveryTimeSlot,
              address: orderFormData?.deliveryAddress,
              city: orderFormData?.city,
              postalCode: orderFormData?.postalCode,
              isPromo: isPromoOrder
            })
          }
        );

        if (!emailResponse?.ok) {
          console.error('Email sending failed:', await emailResponse?.text());
        }
      } catch (emailError) {
        console.error('Email error:', emailError);
      }

      alert(`✅ Commande confirmée !

Votre numéro de référence : ${reference}

Vous recevrez un email de confirmation avec tous les détails de votre commande.`);

      handleCloseModal();

      // Reset form
      setOrderFormData({
        quantity: 1,
        customerName: '',
        customerEmail: '',
        customerPhone: '',
        deliveryAddress: '',
        postalCode: '',
        city: '',
        floor: '',
        deliveryDate: '',
        deliveryTimeSlot: 'Matin 8h-12h',
        paymentMethod: 'Virement bancaire',
        acceptTerms: false
      });

    } catch (error) {
      console.error('Error submitting order:', error);
      alert('Une erreur est survenue lors de l\'enregistrement de votre commande. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  const faqItems = [
    {
      question: 'Comment se passe la livraison de la barrique vide ?',
      answer: 'Nous livrons votre barrique vide directement chez vous à Paris ou en banlieue. Vous choisissez la date qui vous convient lors de la commande.'
    },
    {
      question: 'Combien de temps ai-je pour remplir ma barrique ?',
      answer: 'Vous avez généralement 2 à 3 semaines pour remplir votre barrique avant la date d\'enlèvement. Nous vous contacterons pour planifier la collecte.'
    },
    {
      question: 'Que puis-je mettre dans ma barrique ?',
      answer: 'Vêtements, chaussures, électroménager, produits alimentaires non périssables, jouets, fournitures scolaires, et bien plus. Les produits dangereux et périssables ne sont pas acceptés.'
    },
    {
      question: 'Quand part le prochain conteneur ?',
      answer: 'Nous avons 2 départs par mois vers Abidjan. Le prochain départ est prévu prochainement. Commandez maintenant pour être sûr d\'avoir votre place.'
    }
  ];

  const structuredDataOrder = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebPage",
        "name": "Commander un envoi vers la Côte d\'Ivoire",
        "description": "Réservez votre barrique ou conteneur vers Abidjan en ligne",
        "url": "https://www.adtexpress.fr/commander"
      },
      {
        "@type": "BreadcrumbList",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Accueil",
            "item": "https://www.adtexpress.fr"
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "Commander",
            "item": "https://www.adtexpress.fr/commander"
          }
        ]
      }
    ]
  };

  return (
    <>
      <SEOHelmet
        title="Commander Envoi Barrique Abidjan | Réserver Conteneur France Côte d'Ivoire - ADT Express"
        description="Commandez votre envoi vers la Côte d'Ivoire en ligne : barrique Abidjan, conteneur France CI, fret maritime Paris Abidjan. Réservation simple, paiement sécurisé, collecte à domicile."
        keywords={`commander barrique Abidjan, réserver envoi Côte d'Ivoire, acheter barrique diaspora ivoirienne, réservation conteneur France CI, commande fret maritime Paris Abidjan, envoyer barrique Côte d'Ivoire, groupage maritime, tarif envoi Abidjan`}
        canonicalUrl="https://www.adtexpress.fr/commander"
        structuredData={structuredDataOrder}
      />
      <Helmet>
        <title>Commander votre barrique ou carton - ADT Express</title>
        <meta name="description" content="Commandez votre barrique ou carton pour envoyer vos affaires en Côte d'Ivoire. Barriques noires 270L, bleues 220L, cartons triple cannelure. Réservez votre enlèvement en ligne." />
      </Helmet>
      <div className="min-h-screen bg-white pt-44">
        <PublicNavigation />

        {/* Promo Banner for Commander Page */}
        {showPromoBanner && (
          <div className="bg-gradient-to-r from-orange-50 to-orange-100 border-b-2 border-orange-200">
            <div className="max-w-7xl mx-auto px-4 py-6">
              <div className="flex flex-col gap-4">
                {/* Header */}
                <div className="flex items-center gap-3">
                  <span className="text-3xl">🎉</span>
                  <div>
                    <p className="text-xl font-bold text-gray-900">
                      🌞 Offre Été 2026 : Deux offres au choix
                    </p>
                    <p className="text-sm text-gray-600">
                      Valable jusqu'en septembre 2026
                    </p>
                  </div>
                </div>

                {/* Two Offers */}
                <div className="grid md:grid-cols-2 gap-4">
                  {/* Offer 1 - Promo Été 2026 */}
                  <div className="bg-white border-2 border-green-500 rounded-lg p-4 md:col-span-2">
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <p className="text-lg font-bold text-gray-900">🎁 Offre Été 2026 — Tout compris</p>
                        <p className="text-sm text-gray-600">Barrique noire 270L offerte + transport complet France → Abidjan</p>
                      </div>
                      <div className="text-3xl font-bold text-green-600">160€</div>
                    </div>
                    <p className="text-xs text-gray-600 mb-3">
                      Transport Paris-Abidjan + livraison incluse + barrique noire 270L offerte
                    </p>
                    <p className="text-xs text-gray-500 italic">
                      Valable jusqu'en septembre 2026 — Paiement flexible 60/40
                    </p>
                  </div>
                </div>

                {/* CTA */}
                <div className="flex justify-center">
                  <Button
                    onClick={handlePromoSelection}
                    className="bg-orange-500 hover:bg-orange-600 text-white font-semibold px-8 py-3 rounded-lg shadow-md"
                  >
                    Sélectionner cette offre
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Header Section */}
        <motion.section
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8 }}
          className="relative h-[400px] flex items-center justify-center overflow-hidden"
        >
          <div className="absolute inset-0 bg-gradient-to-br from-green-900 via-green-800 to-green-900 opacity-95"></div>
          <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1494412651409-8963ce7935a7?w=1200')] bg-cover bg-center opacity-20"></div>
          
          <div className="relative z-10 max-w-4xl mx-auto px-4 text-center text-white">
            <motion.h1
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.8 }}
              className="text-4xl md:text-5xl font-bold mb-6"
            >
              Commandez votre barrique ou carton et envoyez vos affaires en Côte d'Ivoire
            </motion.h1>
            <motion.p
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.4, duration: 0.8 }}
              className="text-lg md:text-xl text-green-50"
            >
              Choisissez votre barrique ou carton, réservez votre livraison, et on s'occupe du reste.
            </motion.p>
          </div>
        </motion.section>

        {/* Tab Navigation */}
        <section className="bg-gray-50 border-b-2 border-gray-200">
          <div className="max-w-7xl mx-auto px-4 py-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Tab 1: Commander contenant vide */}
              <button
                onClick={() => setActiveTab('order')}
                className={`p-6 rounded-xl border-2 transition-all text-left ${
                  activeTab === 'order' ? 'bg-green-600 border-green-600 text-white shadow-lg' : 'bg-white border-gray-300 text-gray-900 hover:border-green-500'
                }`}
              >
                <div className="flex items-center gap-3 mb-2">
                  <Icon name="Package" size={32} className={activeTab === 'order' ? 'text-white' : 'text-green-600'} />
                  <h2 className="text-2xl font-bold">
                    📦 Je commande une barrique ou un carton vide
                  </h2>
                </div>
                <p className={`text-sm ${
                  activeTab === 'order' ? 'text-green-50' : 'text-gray-600'
                }`}>
                  Recevez votre contenant vide chez vous pour le remplir
                </p>
              </button>

              {/* Tab 2: Réserver enlèvement */}
              <button
                onClick={() => setActiveTab('pickup')}
                className={`p-6 rounded-xl border-2 transition-all text-left ${
                  activeTab === 'pickup' ? 'bg-green-600 border-green-600 text-white shadow-lg' : 'bg-white border-gray-300 text-gray-900 hover:border-green-500'
                }`}
              >
                <div className="flex items-center gap-3 mb-2">
                  <Icon name="Truck" size={32} className={activeTab === 'pickup' ? 'text-white' : 'text-green-600'} />
                  <h2 className="text-2xl font-bold">
                    🚛 Je réserve l'enlèvement de mon colis
                  </h2>
                </div>
                <p className={`text-sm ${
                  activeTab === 'pickup' ? 'text-green-50' : 'text-gray-600'
                }`}>
                  Votre barrique ou carton est rempli ? On vient le chercher
                </p>
              </button>
            </div>
          </div>
        </section>

        {/* Tab Content */}
        <AnimatePresence mode="wait">
          {activeTab === 'order' && (
            <motion.div
              key="order-tab"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
            >
              {/* Container Cards Section */}
              <section className="max-w-7xl mx-auto px-4 py-16">
                <motion.h2
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  className="text-3xl font-bold text-gray-900 text-center mb-12"
                >
                  Choisissez votre contenant
                </motion.h2>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {containerCards?.map((card, index) => (
                    <motion.div
                      key={card?.id}
                      initial={{ opacity: 0, y: 30 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.1 }}
                      className="bg-white border-2 border-gray-200 rounded-xl p-6 hover:shadow-xl transition-all duration-300 hover:border-green-500"
                    >
                      <div className={`inline-block px-3 py-1 rounded-full text-white text-sm font-semibold mb-4 ${card?.badgeColor}`}>
                        {card?.badge}
                      </div>
                      
                      <div className="flex items-center gap-3 mb-4">
                        <Icon name={card?.icon} size={32} className="text-green-600" />
                        <div>
                          <h3 className="text-xl font-bold text-gray-900">{card?.title}</h3>
                          <p className="text-sm text-gray-600">{card?.capacity}</p>
                        </div>
                      </div>

                      <div className="space-y-2 mb-4">
                        {card?.prices?.map((price, idx) => (
                          <div key={idx} className="flex justify-between text-sm">
                            <span className="text-gray-600">{price?.label}</span>
                            <span className="font-semibold text-gray-900">{price?.value}€</span>
                          </div>
                        ))}
                      </div>

                      <div className="border-t pt-4 mb-4">
                        <div className="flex justify-between items-center">
                          <span className="text-gray-700 font-semibold">Total à partir de :</span>
                          <span className="text-2xl font-bold text-green-600">{card?.totalFrom}€</span>
                        </div>
                      </div>

                      <div className="space-y-2 mb-6">
                        <p className="text-sm font-semibold text-gray-700">Points forts :</p>
                        <ul className="space-y-1">
                          {card?.features?.map((feature, idx) => (
                            <li key={idx} className="text-sm text-gray-600 flex items-start gap-2">
                              <Icon name="Check" size={16} className="text-green-600 mt-0.5 flex-shrink-0" />
                              <span>{feature}</span>
                            </li>
                          ))}
                        </ul>
                      </div>

                      <Button
                        onClick={() => handleOpenOrderModal(card)}
                        className="w-full bg-green-600 hover:bg-green-700 text-white"
                      >
                        Commander {card?.title?.toLowerCase()}
                      </Button>
                    </motion.div>
                  ))}
                </div>
              </section>
            </motion.div>
          )}

          {activeTab === 'pickup' && (
            <motion.div
              key="pickup-tab"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
            >
              {/* Pickup Reservation Form Section */}
              <section className="bg-gray-50 py-16">
                <div className="max-w-4xl mx-auto px-4">
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    className="bg-white rounded-xl shadow-lg p-8"
                  >
                    <div className="text-center mb-8">
                      <h2 className="text-3xl font-bold text-gray-900 mb-2">
                        🚛 Réservez la collecte de votre colis
                      </h2>
                      <p className="text-gray-600">
                        Nous venons chercher votre barrique ou carton rempli directement chez vous. Remplissez ce formulaire et nous organisons l'enlèvement.
                      </p>
                    </div>

                    <form onSubmit={handlePickupSubmit} className="space-y-8">
                      {/* Step 1: Customer Info */}
                      <div>
                        <h3 className="text-xl font-bold text-gray-900 mb-4">Étape 1 — Vos coordonnées</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1.5">
                              Nom complet *
                            </label>
                            <input
                              type="text"
                              value={pickupFormData?.customerName}
                              onChange={handlePickupInputChange('customerName')}
                              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-gray-900 placeholder:text-gray-400"
                              placeholder="Jean Dupont"
                              required
                            />
                            {errors?.customerName && (
                              <p className="text-sm text-red-600 mt-1">{errors?.customerName}</p>
                            )}
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1.5">
                              Email *
                            </label>
                            <input
                              type="email"
                              value={pickupFormData?.customerEmail}
                              onChange={handlePickupInputChange('customerEmail')}
                              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-gray-900 placeholder:text-gray-400"
                              placeholder="email@example.com"
                              required
                            />
                            {errors?.customerEmail && (
                              <p className="text-sm text-red-600 mt-1">{errors?.customerEmail}</p>
                            )}
                          </div>

                          <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-gray-700 mb-1.5">
                              Téléphone *
                            </label>
                            <input
                              type="tel"
                              value={pickupFormData?.customerPhone}
                              onChange={handlePickupInputChange('customerPhone')}
                              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-gray-900 placeholder:text-gray-400"
                              placeholder="+33 6 12 34 56 78"
                              required
                            />
                            {errors?.customerPhone && (
                              <p className="text-sm text-red-600 mt-1">{errors?.customerPhone}</p>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Step 2: Pickup Address */}
                      <div>
                        <h3 className="text-xl font-bold text-gray-900 mb-4">Étape 2 — Adresse d'enlèvement</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-gray-700 mb-1.5">
                              Adresse complète *
                            </label>
                            <textarea
                              value={pickupFormData?.customerAddress}
                              onChange={handlePickupInputChange('customerAddress')}
                              rows={3}
                              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none"
                              placeholder="Numéro, rue..."
                            />
                            {errors?.customerAddress && (
                              <p className="text-sm text-red-600 mt-1">{errors?.customerAddress}</p>
                            )}
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1.5">
                              Code postal *
                            </label>
                            <input
                              type="text"
                              value={pickupFormData?.postalCode}
                              onChange={handlePickupInputChange('postalCode')}
                              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-gray-900 placeholder:text-gray-400"
                              placeholder="75001"
                              required
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1.5">
                              Ville *
                            </label>
                            <input
                              type="text"
                              value={pickupFormData?.customerCity}
                              onChange={handlePickupInputChange('customerCity')}
                              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-gray-900 placeholder:text-gray-400"
                              placeholder="Paris"
                              required
                            />
                            {errors?.customerCity && (
                              <p className="text-sm text-red-600 mt-1">{errors?.customerCity}</p>
                            )}
                          </div>

                          <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-gray-700 mb-1.5">
                              Étage / digicode / instructions (optionnel)
                            </label>
                            <input
                              type="text"
                              value={pickupFormData?.floor}
                              onChange={handlePickupInputChange('floor')}
                              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-gray-900 placeholder:text-gray-400"
                              placeholder="3ème étage, code 1234"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Step 3: Articles Selection */}
                      <div>
                        <h3 className="text-xl font-bold text-gray-900 mb-4">Étape 3 — Colis à enlever</h3>
                        
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                          <p className="text-sm text-blue-800">
                            💡 Sélectionnez les articles depuis notre catalogue complet. Les prix incluent le transport vers Abidjan.
                          </p>
                        </div>

                        <div className="space-y-4">
                          <div className="flex gap-2">
                            <select
                              value={selectedCatalogItemId}
                              onChange={(e) => setSelectedCatalogItemId(e?.target?.value)}
                              className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                              disabled={loadingCatalog}
                            >
                              <option value="">Sélectionner un article</option>
                              {catalogItems?.map(item => (
                                <option key={item?.id} value={item?.id}>
                                  {item?.name} - {item?.price}€
                                </option>
                              ))}
                            </select>
                            <Button
                              type="button"
                              onClick={handleAddArticle}
                              disabled={!selectedCatalogItemId || loadingCatalog}
                              className="bg-green-600 hover:bg-green-700 text-white px-6"
                            >
                              Ajouter
                            </Button>
                          </div>

                          {errors?.articles && (
                            <p className="text-sm text-red-600">{errors?.articles}</p>
                          )}

                          {/* Selected Articles Table */}
                          {selectedArticles?.length > 0 && (
                            <div className="border border-gray-200 rounded-lg overflow-hidden">
                              <table className="w-full">
                                <thead className="bg-gray-50">
                                  <tr>
                                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Article</th>
                                    <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">Quantité</th>
                                    <th className="px-4 py-3 text-right text-sm font-semibold text-gray-700">Prix unit.</th>
                                    <th className="px-4 py-3 text-right text-sm font-semibold text-gray-700">Sous-total</th>
                                    <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">Action</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200">
                                  {selectedArticles?.map(article => (
                                    <tr key={article?.catalogItemId}>
                                      <td className="px-4 py-3 text-sm text-gray-900">{article?.name}</td>
                                      <td className="px-4 py-3 text-center">
                                        <div className="flex items-center justify-center gap-2">
                                          <button
                                            type="button"
                                            onClick={() => handleQuantityChange(article?.catalogItemId, -1)}
                                            className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-200 hover:bg-gray-300 text-gray-700"
                                          >
                                            -
                                          </button>
                                          <span className="w-8 text-center font-semibold">{article?.quantity}</span>
                                          <button
                                            type="button"
                                            onClick={() => handleQuantityChange(article?.catalogItemId, 1)}
                                            className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-200 hover:bg-gray-300 text-gray-700"
                                          >
                                            +
                                          </button>
                                        </div>
                                      </td>
                                      <td className="px-4 py-3 text-right text-sm text-gray-900">{article?.price}€</td>
                                      <td className="px-4 py-3 text-right text-sm font-semibold text-gray-900">
                                        {(article?.price * article?.quantity)?.toFixed(2)}€
                                      </td>
                                      <td className="px-4 py-3 text-center">
                                        <button
                                          type="button"
                                          onClick={() => handleRemoveArticle(article?.catalogItemId)}
                                          className="text-red-600 hover:text-red-800"
                                        >
                                          <Icon name="Trash2" size={18} />
                                        </button>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                                <tfoot className="bg-gray-50">
                                  <tr>
                                    <td colSpan="3" className="px-4 py-3 text-right text-base font-bold text-gray-900">
                                      Total enlèvement :
                                    </td>
                                    <td className="px-4 py-3 text-right text-xl font-bold text-green-600">
                                      {calculatePickupTotal()?.toFixed(2)}€
                                    </td>
                                    <td></td>
                                  </tr>
                                </tfoot>
                              </table>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Step 4: Pickup Date */}
                      <div>
                        <h3 className="text-xl font-bold text-gray-900 mb-4">Étape 4 — Date d'enlèvement</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1.5">
                              Date souhaitée d'enlèvement *
                            </label>
                            <input
                              type="date"
                              value={pickupFormData?.deliveryDate}
                              onChange={handlePickupInputChange('deliveryDate')}
                              min={new Date()?.toISOString()?.split('T')?.[0]}
                              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                            />
                            {errors?.deliveryDate && (
                              <p className="text-sm text-red-600 mt-1">{errors?.deliveryDate}</p>
                            )}
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1.5">
                              Créneau préféré
                            </label>
                            <select
                              value={pickupFormData?.deliveryTimeSlot}
                              onChange={handlePickupInputChange('deliveryTimeSlot')}
                              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                            >
                              <option value="Matin 8h-12h">Matin 8h-12h</option>
                              <option value="Après-midi 13h-17h">Après-midi 13h-17h</option>
                              <option value="Pas de préférence">Pas de préférence</option>
                            </select>
                          </div>
                        </div>
                      </div>

                      {/* Step 5: Confirmation */}
                      <div>
                        <h3 className="text-xl font-bold text-gray-900 mb-4">Étape 5 — Confirmation</h3>
                        
                        {/* Summary */}
                        <div className="bg-gray-50 rounded-lg p-6 mb-4">
                          <h4 className="font-bold text-gray-900 mb-3">Récapitulatif de votre réservation</h4>
                          <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                              <span className="text-gray-600">Nom :</span>
                              <span className="font-medium text-gray-900">{pickupFormData?.customerName || '-'}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600">Email :</span>
                              <span className="font-medium text-gray-900">{pickupFormData?.customerEmail || '-'}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600">Téléphone :</span>
                              <span className="font-medium text-gray-900">{pickupFormData?.customerPhone || '-'}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600">Adresse :</span>
                              <span className="font-medium text-gray-900 text-right">{pickupFormData?.customerAddress || '-'}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600">Ville :</span>
                              <span className="font-medium text-gray-900">{pickupFormData?.customerCity || '-'}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600">Date d'enlèvement :</span>
                              <span className="font-medium text-gray-900">{pickupFormData?.deliveryDate || '-'}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600">Créneau :</span>
                              <span className="font-medium text-gray-900">{pickupFormData?.deliveryTimeSlot}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600">Nombre d'articles :</span>
                              <span className="font-medium text-gray-900">{selectedArticles?.length}</span>
                            </div>
                            <div className="border-t pt-2 mt-2 flex justify-between">
                              <span className="font-bold text-gray-900">Total :</span>
                              <span className="font-bold text-green-600 text-lg">{calculatePickupTotal()?.toFixed(2)}€</span>
                            </div>
                          </div>
                        </div>

                        <div className="space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1.5">
                              Mode de paiement
                            </label>
                            <select
                              value={pickupFormData?.paymentMethod}
                              onChange={handlePickupInputChange('paymentMethod')}
                              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                            >
                              <option value="Virement bancaire">Virement bancaire</option>
                              <option value="Paiement à la livraison">Paiement à la livraison</option>
                              <option value="Autre">Autre</option>
                            </select>
                          </div>

                          <div className="flex items-start gap-2">
                            <input
                              type="checkbox"
                              id="pickupAcceptTerms"
                              checked={pickupFormData?.acceptTerms}
                              onChange={handlePickupInputChange('acceptTerms')}
                              className="mt-1 w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                            />
                            <label htmlFor="pickupAcceptTerms" className="text-sm text-gray-700">
                              J'accepte les conditions générales de vente *
                            </label>
                          </div>
                          {errors?.acceptTerms && (
                            <p className="text-sm text-red-600">{errors?.acceptTerms}</p>
                          )}
                        </div>

                        <Button
                          type="submit"
                          disabled={loading}
                          className="w-full bg-green-600 hover:bg-green-700 text-white text-lg py-4 mt-6"
                        >
                          {loading ? 'Envoi en cours...' : 'Confirmer ma réservation d\'enlèvement'}
                        </Button>

                        <p className="text-center text-sm text-gray-600 mt-2">
                          Vous recevrez un email de confirmation avec votre numéro de référence et les détails de votre réservation d'enlèvement.
                        </p>
                      </div>
                    </form>
                  </motion.div>
                </div>
              </section>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Questions Section - Always visible */}
        <section className="bg-gray-50 py-16">
          <div className="max-w-4xl mx-auto px-4">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-3xl font-bold text-gray-900 text-center mb-12"
            >
              Vous avez des questions avant de commander ?
            </motion.h2>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {/* Call Card */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="bg-white border-2 border-gray-200 rounded-xl p-6 text-center hover:shadow-lg transition-all"
              >
                <div className="text-4xl mb-4">📞</div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Appelez un conseiller</h3>
                <p className="text-gray-600 text-sm mb-4">
                  Notre équipe est disponible pour répondre à toutes vos questions et vous accompagner dans votre commande.
                </p>
                <p className="text-2xl font-bold text-green-600 mb-2">06 51 98 86 21</p>
                <p className="text-sm text-gray-500 mb-4">Lundi - Samedi, 9h - 19h</p>
                <Button
                  onClick={() => window.open('tel:+33651988621', '_self')}
                  className="w-full bg-green-600 hover:bg-green-700 text-white"
                >
                  Appeler maintenant
                </Button>
              </motion.div>

              {/* WhatsApp Card */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                className="bg-white border-2 border-gray-200 rounded-xl p-6 text-center hover:shadow-lg transition-all"
              >
                <div className="text-4xl mb-4">💬</div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Écrivez-nous sur WhatsApp</h3>
                <p className="text-gray-600 text-sm mb-4">
                  Envoyez-nous un message et recevez une réponse rapide. Partagez-nous des photos, posez vos questions.
                </p>
                <Button
                  onClick={() => {
                    const message = encodeURIComponent('Bonjour, je souhaite commander une barrique pour un envoi vers Abidjan.');
                    window.open(`https://wa.me/33651988621?text=${message}`, '_blank');
                  }}
                  className="w-full bg-green-600 hover:bg-green-700 text-white"
                >
                  Ouvrir WhatsApp
                </Button>
              </motion.div>

              {/* Chatbot Card */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
                className="bg-white border-2 border-gray-200 rounded-xl p-6 text-center hover:shadow-lg transition-all"
              >
                <div className="text-4xl mb-4">🤖</div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Discutez avec Fatou, notre assistante</h3>
                <p className="text-gray-600 text-sm mb-4">
                  Fatou peut répondre à vos questions 24h/24 et même prendre votre commande directement dans le chat.
                </p>
                <Button
                  onClick={() => {
                    // Trigger chatbot opening
                    const chatButton = document.querySelector('[data-chat-widget]');
                    if (chatButton) chatButton?.click();
                  }}
                  className="w-full bg-green-600 hover:bg-green-700 text-white"
                >
                  Parler à Fatou
                </Button>
              </motion.div>
            </div>
          </div>
        </section>

        {/* FAQ Section - Always visible */}
        <section className="bg-gray-50 py-16">
          <div className="max-w-4xl mx-auto px-4">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-3xl font-bold text-gray-900 text-center mb-12"
            >
              FAQ rapide commande
            </motion.h2>

            <div className="space-y-4">
              {faqItems?.map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white rounded-lg shadow-sm overflow-hidden"
                >
                  <button
                    onClick={() => setOpenFaq(openFaq === index ? null : index)}
                    className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-gray-50 transition-colors"
                  >
                    <span className="font-semibold text-gray-900">{item?.question}</span>
                    <Icon
                      name={openFaq === index ? 'ChevronUp' : 'ChevronDown'}
                      size={20}
                      className="text-gray-500 flex-shrink-0"
                    />
                  </button>
                  {openFaq === index && (
                    <div className="px-6 pb-4 text-gray-600">
                      {item?.answer}
                    </div>
                  )}
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Final CTA Banner - Always visible */}
        <section className="bg-gradient-to-r from-green-800 to-green-900 py-12">
          <div className="max-w-7xl mx-auto px-4 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <p className="text-white text-xl md:text-2xl font-bold mb-4">
🚢 Prochain départ vers Abidjan : Bientôt — Il reste des places
              </p>
              <Button
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                className="bg-white text-green-900 hover:bg-gray-100 font-semibold px-8 py-3"
              >
Commander maintenant
              </Button>
            </motion.div>
          </div>
        </section>

        {/* Order Modal */}
        <AnimatePresence>
          {isModalOpen && selectedProduct && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
              onClick={handleCloseModal}
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                onClick={(e) => e?.stopPropagation()}
                className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto"
              >
                <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between z-10">
                  <h2 className="text-2xl font-bold text-gray-900">
                    Commander {selectedProduct?.title}
                  </h2>
                  <button
                    onClick={handleCloseModal}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    <Icon name="X" size={24} />
                  </button>
                </div>

                <div className="p-6">
                  {/* Product Summary */}
                  <div className="bg-gray-50 rounded-lg p-4 mb-6 flex items-center gap-4">
                    <Icon name={selectedProduct?.icon} size={48} className="text-green-600" />
                    <div className="flex-1">
                      <h3 className="font-bold text-lg text-gray-900">{selectedProduct?.title}</h3>
                      <p className="text-sm text-gray-600">{selectedProduct?.capacity}</p>
                      {isPromoOrder && selectedProduct?.id === 'barrique-noire' ? (
                        <div>
                          <p className="text-lg font-bold text-orange-600 mt-1">
                            🎉 PROMO : 150€ TTC (barrique offerte + transport)
                          </p>
                          <p className="text-xs text-gray-500 line-through">Prix normal : 305€</p>
                        </div>
                      ) : (
                        <p className="text-lg font-bold text-green-600 mt-1">
                          À partir de {selectedProduct?.basePrice}€
                        </p>
                      )}
                    </div>
                  </div>

                  <form onSubmit={handleOrderSubmit} className="space-y-6">
                    {/* Quantity */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Quantité
                      </label>
                      <div className="flex items-center gap-3">
                        <button
                          type="button"
                          onClick={() => setOrderFormData(prev => ({ ...prev, quantity: Math.max(1, prev?.quantity - 1) }))}
                          className="w-10 h-10 flex items-center justify-center rounded-full bg-gray-200 hover:bg-gray-300 text-gray-700 font-bold"
                        >
                          -
                        </button>
                        <span className="text-xl font-bold text-gray-900 w-12 text-center">{orderFormData?.quantity}</span>
                        <button
                          type="button"
                          onClick={() => setOrderFormData(prev => ({ ...prev, quantity: prev?.quantity + 1 }))}
                          className="w-10 h-10 flex items-center justify-center rounded-full bg-gray-200 hover:bg-gray-300 text-gray-700 font-bold"
                        >
                          +
                        </button>
                      </div>
                    </div>

                    {/* Customer Info */}
                    <div>
                      <h3 className="text-lg font-bold text-gray-900 mb-3">Vos coordonnées</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1.5">
                            Nom complet *
                          </label>
                          <input
                            type="text"
                            value={orderFormData?.customerName}
                            onChange={handleOrderInputChange('customerName')}
                            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-gray-900 placeholder:text-gray-400"
                            placeholder="Jean Dupont"
                            required
                          />
                          {errors?.customerName && (
                            <p className="text-sm text-red-600 mt-1">{errors?.customerName}</p>
                          )}
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1.5">
                            Email *
                          </label>
                          <input
                            type="email"
                            value={orderFormData?.customerEmail}
                            onChange={handleOrderInputChange('customerEmail')}
                            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-gray-900 placeholder:text-gray-400"
                            placeholder="email@example.com"
                            required
                          />
                          {errors?.customerEmail && (
                            <p className="text-sm text-red-600 mt-1">{errors?.customerEmail}</p>
                          )}
                        </div>

                        <div className="md:col-span-2">
                          <label className="block text-sm font-medium text-gray-700 mb-1.5">
                            Téléphone *
                          </label>
                          <input
                            type="tel"
                            value={orderFormData?.customerPhone}
                            onChange={handleOrderInputChange('customerPhone')}
                            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-gray-900 placeholder:text-gray-400"
                            placeholder="+33 6 12 34 56 78"
                            required
                          />
                          {errors?.customerPhone && (
                            <p className="text-sm text-red-600 mt-1">{errors?.customerPhone}</p>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Delivery Address */}
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 mb-4">Étape 4 — Date et créneau de livraison</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1.5">
                            Date souhaitée *
                          </label>
                          <input
                            type="date"
                            value={orderFormData?.deliveryDate}
                            onChange={handleOrderInputChange('deliveryDate')}
                            min={new Date()?.toISOString()?.split('T')?.[0]}
                            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                          />
                          {errors?.deliveryDate && (
                            <p className="text-sm text-red-600 mt-1">{errors?.deliveryDate}</p>
                          )}
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1.5">
                            Créneau préféré *
                          </label>
                          <select
                            value={orderFormData?.deliveryTimeSlot}
                            onChange={handleOrderInputChange('deliveryTimeSlot')}
                            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                          >
                            <option value="Matin 8h-12h">Matin 8h-12h</option>
                            <option value="Après-midi 13h-17h">Après-midi 13h-17h</option>
                            <option value="Pas de préférence">Pas de préférence</option>
                          </select>
                        </div>
                      </div>
                    </div>

                    {/* Summary */}
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h4 className="font-bold text-gray-900 mb-3">Récapitulatif</h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Article :</span>
                          <span className="font-medium text-gray-900">{selectedProduct?.title} x {orderFormData?.quantity}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Prix unitaire :</span>
                          <span className="font-medium text-gray-900">
                            {selectedProduct?.id === 'barrique-noire' && orderFormData?.postalCode ? (orderFormData?.postalCode?.startsWith('75') && orderFormData?.postalCode?.length === 5
                                ? '25€' :'30€')
                              : `${selectedProduct?.basePrice}€`}
                          </span>
                        </div>
                        <div className="border-t pt-2 flex justify-between">
                          <span className="font-bold text-gray-900">Total :</span>
                          <span className="font-bold text-green-600 text-xl">{calculateOrderTotal()}€</span>
                        </div>
                      </div>
                    </div>

                    {/* Payment Method */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">
                        Mode de paiement
                      </label>
                      <select
                        value={orderFormData?.paymentMethod}
                        onChange={handleOrderInputChange('paymentMethod')}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      >
                        <option value="Virement bancaire">Virement bancaire</option>
                        <option value="Paiement à la livraison">Paiement à la livraison</option>
                        <option value="Autre">Autre</option>
                      </select>
                    </div>

                    {/* Terms */}
                    <div className="flex items-start gap-2">
                      <input
                        type="checkbox"
                        id="orderAcceptTerms"
                        checked={orderFormData?.acceptTerms}
                        onChange={handleOrderInputChange('acceptTerms')}
                        className="mt-1 w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                      />
                      <label htmlFor="orderAcceptTerms" className="text-sm text-gray-700">
                        J'accepte les conditions générales de vente *
                      </label>
                    </div>
                    {errors?.acceptTerms && (
                      <p className="text-sm text-red-600">{errors?.acceptTerms}</p>
                    )}

                    {/* Submit Button */}
                    <Button
                      type="submit"
                      disabled={loading}
                      className="w-full bg-green-600 hover:bg-green-700 text-white text-lg py-4"
                    >
                      {loading ? 'Envoi en cours...' : 'Valider ma commande'}
                    </Button>

                    <p className="text-center text-sm text-gray-600">
                      Vous recevrez un email de confirmation avec votre numéro de référence
                    </p>
                  </form>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        <UnifiedFooter />
      </div>
    </>
  );
}